import React, { useState, useEffect } from 'react';

import * as Yup from 'yup';
import { Col, Form, Row, Table, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUndo } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { FormikHelpers, getIn, useFormik } from 'formik';
import { AxiosResponse } from 'axios';
import { MyApplicationDetail } from '../../api/openapi';
import { setYupLocale } from '../../utils/formUtils';
import { applicationApi } from '../../api';

interface Props {
  item: MyApplicationDetail;
  onSaveSuccess: () => void;
}

type FileWithPathOrExisting =
  | FileWithPath
  | {
      id: string;
      path: string;
      name: string;
    };

interface Values {
  industryPensionFundRequired: boolean;
  files: FileWithPathOrExisting[];
  filesExtra: {
    isPensionFundFile: boolean;
    isDelete: boolean;
  }[];
}

const ApplicationFilesUpload: React.FC<Props> = ({ item, onSaveSuccess }: Props) => {
  const { t, i18n } = useTranslation(['viewApplication', 'common']);
  const [validationSchema, setValidationSchema] = useState<Yup.AnySchema | null>(null);
  const formik = useFormik<Values>({
    initialValues: {
      industryPensionFundRequired: false,
      files: item.files.map((f) => ({
        id: f.id,
        path: f.name,
        name: f.name,
      })),
      filesExtra: item.files.map((f) => ({
        isPensionFundFile: !!f.isPensionfundFile,
        isDelete: false,
      })),
    },
    validateOnBlur: false,
    // validateOnChange={false}
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
      try {
        await Promise.all<AxiosResponse>(
          values.files.map((f, idx) => {
            if ('id' in f) {
              if (values.filesExtra[idx].isDelete) {
                return applicationApi.myApplicationFileDestroy({
                  uuid: f.id,
                  application: item.id,
                });
              }

              return applicationApi.myApplicationFilePartialUpdate({
                application: item.id,
                uuid: f.id,
                patchedMyApplicationFileRequest: {
                  isPensionfundFile: values.filesExtra[idx].isPensionFundFile,
                },
              });
            }

            return applicationApi.applicationMyApplicationUploadFile({
              uuid: item.id,
              name: f.name,
              file: f,
              isPensionfundFile: values.filesExtra[idx].isPensionFundFile,
            });
          })
        );

        toast.success(t('applicationUpdate'));

        onSaveSuccess();
      } catch (e) {
        toast.error(t('common:internalError'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    setYupLocale(Yup, i18n.language);
    setValidationSchema(
      Yup.object().shape({
        industryPensionFundRequired: Yup.boolean().required(),
        // eslint-disable-next-line react/forbid-prop-types
        files: Yup.array(),
        filesExtra: Yup.array().when('industryPensionFundRequired', (industryPensionFundRequired: boolean, schema: Yup.ArraySchema<Yup.AnySchema>) => {
          return !industryPensionFundRequired
            ? schema
            : schema.test('has-pension-fund-file', t('onePensionfundFile'), (v) => !!v && v.filter((x) => x.isPensionFundFile).length > 0);
        }),
      })
    );
  }, [t, i18n.language]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const newFiles = [...formik.values.files, ...acceptedFiles];
      const newExtra = [
        ...formik.values.filesExtra,
        ...acceptedFiles.map(() => ({
          isPensionFundFile: false,
          isDelete: false,
        })),
      ];
      formik.setFieldValue('filesExtra', newExtra);
      formik.setFieldValue('files', newFiles);
    },
  });

  const removeFile = (idx: number) => {
    const extraTemp = [...formik.values.filesExtra];

    if ('id' in formik.values.files[idx]) {
      extraTemp[idx] = {
        ...extraTemp[idx],
        isDelete: !extraTemp[idx].isDelete,
      };
    } else {
      const filesTemp = [...formik.values.files];
      filesTemp.splice(idx, 1);
      extraTemp.splice(idx, 1);
      formik.setFieldValue('files', filesTemp);
    }

    formik.setFieldValue('filesExtra', extraTemp);
  };

  const renderFiles = formik.values.files.map((file, idx) => (
    <tr key={file.path}>
      <td
        style={{
          textDecoration: formik.values.filesExtra[idx].isDelete ? 'line-through' : undefined,
        }}
      >
        {file.path}
      </td>

      <td>
        {item.industry.requiresPensionfundFile && (
          <Form.Check
            custom
            id={`isPensionfundFile-yes-${idx}`}
            name={`isPensionfundFile-${idx}`}
            inline
            label={t('isPensionfundFile')}
            type="checkbox"
            disabled={formik.values.filesExtra[idx].isDelete}
            checked={formik.values.filesExtra[idx].isPensionFundFile}
            onChange={(e) => formik.setFieldValue(`filesExtra[${idx}].isPensionFundFile`, e.target.checked)}
          />
        )}
      </td>
      <td>
        <Button className="p-0 iconColorRed" variant="link" onClick={() => removeFile(idx)}>
          <FontAwesomeIcon icon={formik.values.filesExtra[idx].isDelete ? faUndo : faTrash} fixedWidth />
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <Form
        className="mt-2"
        onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
          formik.setStatus('submitted');
          formik.handleSubmit(e);
        }}
        noValidate
      >
        <Row>
          <Col md="4">
            <p className="small text-muted">{t('filesAndImagesHelp')}</p>
          </Col>
          <Col md="8">
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <p className="mt-3">{t('dragOrDropFile')}</p>
              {/* <em>{t('onlyTheseFiles')}</em> */}
            </div>
            <aside>
              <Table>
                <tbody>{renderFiles}</tbody>
              </Table>
            </aside>
            {!!getIn(formik.touched, 'filesExtra') && !!getIn(formik.errors, 'filesExtra') && <div>{formik.errors.filesExtra}</div>}
            <hr />
            <Button
              variant="primary"
              onClick={() => {
                formik.setStatus('submitted');
                formik.handleSubmit();
              }}
            >
              {t('common:save')}
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};
export default ApplicationFilesUpload;
